import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from 'components/ui/select';
import { Combobox } from 'components/ui/combobox';
import { Checkbox } from 'components/ui/checkbox';
import { MultiSelectCombobox } from 'components/ui/multi-select-combobox';
import { useAuth } from 'contexts/AuthContext';
import {
  createContract, updateContractDetails, customerOptions, userOptions, ouOptions, customerTerms, Option,
  ContractDetail, TermsOption, PACKAGING_MODES, RATE_TYPES, DIVISION_LEVEL, BRANCH_LEVEL, currentFY,
} from './api';

const termsOptionLabel = (t: TermsOption) => {
  const when = t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '';
  const used = `used by ${t.contracts_count} contract${t.contracts_count === 1 ? '' : 's'}`;
  return [t.label, used, when, t.locked ? 'locked' : ''].filter(Boolean).join(' · ');
};


type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: (contractId: number) => void;
  // edit mode: pre-filled from this contract; the customer and the files can't be changed
  editing?: ContractDetail | null;
  onSaved?: () => void;
};

const UploadContractModal: React.FC<Props> = ({ open, onOpenChange, onCreated, editing, onSaved }) => {
  const [customer, setCustomer] = useState('');
  const [customerDivision, setCustomerDivision] = useState('');
  const [customerLocation, setCustomerLocation] = useState('');
  const [customerOpts, setCustomerOpts] = useState<Option[]>([]);
  const [divisions, setDivisions] = useState<string[]>([]);
  const [divisionOpts, setDivisionOpts] = useState<Option[]>([]);
  const [branchOpts, setBranchOpts] = useState<Option[]>([]);
  const [bookingBranches, setBookingBranches] = useState<string[]>([]);
  const [billingBranches, setBillingBranches] = useState<string[]>([]);
  // like "billing address same as shipping": billing branches follow the booking ones
  const [billingSame, setBillingSame] = useState(false);
  const [userOpts, setUserOpts] = useState<Option[]>([]);
  const [accessUsers, setAccessUsers] = useState<string[]>([]);
  const me = useAuth().userInfo?.ecode;
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rateType, setRateType] = useState('');
  const [materialType, setMaterialType] = useState('');
  const [materialValue, setMaterialValue] = useState('');
  const [packaging, setPackaging] = useState<string[]>([]);
  const [packagingOther, setPackagingOther] = useState('');
  // terms: upload a new main contract document, or reuse one of the customer's existing
  // terms (shared with its other contracts) and upload only this contract's rate sheets
  const [termsMode, setTermsMode] = useState<'new' | 'existing'>('new');
  const [termsOpts, setTermsOpts] = useState<TermsOption[]>([]);
  const [termsId, setTermsId] = useState('');
  const [mainFile, setMainFile] = useState<File | null>(null);
  const [rateSheets, setRateSheets] = useState<File[]>([]);
  const [otherFiles, setOtherFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // load option sets once when opened. Customers = active + TBB set (same as the
  // Customer Health report); the full list loads and the combobox filters client-side.
  useEffect(() => {
    if (!open) return;
    const fy = currentFY();
    customerOptions().then((r) => setCustomerOpts(r.options)).catch(() => {});
    userOptions().then((r) => setUserOpts(r.options)).catch(() => {});
    ouOptions(DIVISION_LEVEL, fy).then((r) => setDivisionOpts(r.options)).catch(() => {});
    ouOptions(BRANCH_LEVEL, fy).then((r) => setBranchOpts(r.options)).catch(() => {});
  }, [open]);

  // the chosen customer's existing terms
  useEffect(() => {
    setTermsOpts([]); setTermsId(''); setTermsMode('new');
    if (!open || editing || !customer) return;
    let cancelled = false;
    customerTerms(customer).then((r) => { if (!cancelled) setTermsOpts(r.terms); }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, editing, customer]);

  // A new upload always starts from a blank form (the component stays mounted between
  // uploads, so the last contract's values would otherwise still be there). User Access
  // decides who can see the contract, so the uploader starts in it (they can remove
  // themselves) — otherwise they'd lose sight of what they just uploaded.
  useEffect(() => {
    if (!open || editing) return;
    setCustomer(''); setCustomerDivision(''); setCustomerLocation('');
    setDivisions([]); setAccessUsers(me ? [me] : []); setBookingBranches([]); setBillingBranches([]); setBillingSame(true);
    setStartDate(''); setEndDate(''); setRateType(''); setMaterialType(''); setMaterialValue('');
    setPackaging([]); setPackagingOther('');
    setMainFile(null); setRateSheets([]); setOtherFiles([]);
    setError(null);
  }, [open, editing, me]);

  // edit mode: start from the contract's current values every time the form opens
  useEffect(() => {
    if (!open || !editing) return;
    const v = editing.current_version;
    setCustomer(editing.customer_code);
    setCustomerDivision(editing.customer_division ?? '');
    setCustomerLocation(editing.customer_location ?? '');
    setDivisions(editing.divisions);
    setAccessUsers(editing.access_users);
    setBookingBranches(editing.booking_branches);
    setBillingBranches(editing.billing_branches);
    const sameSet = (a: string[], b: string[]) => a.length > 0 && a.length === b.length && a.every((x) => b.includes(x));
    setBillingSame(sameSet(editing.booking_branches, editing.billing_branches));
    setStartDate(v?.start_date ?? '');
    setEndDate(v?.end_date ?? '');
    setRateType(v?.rate_type ?? '');
    setMaterialType(v?.material_type ?? '');
    setMaterialValue(v?.material_value_per_kg != null ? String(v.material_value_per_kg) : '');
    setPackaging(v?.packaging_modes ?? []);
    setPackagingOther(v?.packaging_other ?? '');
    setError(null);
  }, [open, editing]);

  const opt = (o: Option[]) => o.map((x) => ({ value: x.code, label: x.name }));
  const billing = billingSame ? bookingBranches : billingBranches;

  const submit = async () => {
    setError(null);
    if (!customer) { setError('Customer is required'); return; }
    if (!startDate || !endDate) { setError('Contract start and end dates are required'); return; }
    if (endDate < startDate) { setError('Contract end date is before the start date'); return; }
    // needed for activation and not editable after upload
    const missing = [
      [divisions.length, 'MRC division'], [bookingBranches.length, 'booking branch'],
      [billing.length, 'billing branch'], [rateType, 'rate type'],
      [materialType.trim(), 'type of material'], [packaging.length, 'mode of packaging'],
    ].filter(([v]) => !v).map(([, label]) => label);
    if (missing.length) { setError(`Required: ${missing.join(', ')}`); return; }
    if (editing) {
      setSubmitting(true);
      try {
        await updateContractDetails(editing.id, {
          customer_division: customerDivision.trim() || null,
          customer_location: customerLocation.trim() || null,
          divisions, access_users: accessUsers, booking_branches: bookingBranches, billing_branches: billing,
          start_date: startDate, end_date: endDate, rate_type: rateType, material_type: materialType.trim(),
          material_value_per_kg: materialValue ? Number(materialValue) : null,
          packaging_modes: packaging, packaging_other: packaging.includes('Other') ? packagingOther.trim() || null : null,
        });
        onSaved?.();
      } catch (e: any) {
        setError(e?.message || 'Save failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (termsMode === 'new' && !mainFile) { setError('Main contract document is required'); return; }
    if (termsMode === 'existing' && !termsId) { setError('Choose the existing terms to use'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('customer_code', customer);
      if (customerDivision.trim()) fd.append('customer_division', customerDivision.trim());
      if (customerLocation.trim()) fd.append('customer_location', customerLocation.trim());
      divisions.forEach((d) => fd.append('divisions', d));
      accessUsers.forEach((u) => fd.append('access_users', u));
      bookingBranches.forEach((b) => fd.append('booking_branches', b));
      billing.forEach((b) => fd.append('billing_branches', b));
      fd.append('start_date', startDate);
      fd.append('end_date', endDate);
      if (rateType) fd.append('rate_type', rateType);
      if (materialType) fd.append('material_type', materialType);
      if (materialValue) fd.append('material_value_per_kg', materialValue);
      packaging.forEach((m) => fd.append('packaging_modes', m));
      if (packaging.includes('Other') && packagingOther) fd.append('packaging_other', packagingOther);
      if (termsMode === 'existing') fd.append('terms_id', termsId);
      else if (mainFile) fd.append('main_document', mainFile);
      rateSheets.forEach((f) => fd.append('rate_sheets', f));
      otherFiles.forEach((f) => fd.append('other_documents', f));

      const res = await createContract(fd);
      onCreated?.(res.contract_id);
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit contract' : 'Upload contract'}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label>Customer *</Label>
            {editing ? (
              <Input value={editing.customer_name || editing.customer_code} disabled title="The customer can't be changed" />
            ) : (
              <Combobox
                options={opt(customerOpts)}
                value={customer}
                onValueChange={setCustomer}
                placeholder="Select customer…"
                searchPlaceholder="Search customer…"
              />
            )}
          </div>
          <div>
            <Label>Customer Division</Label>
            <Input value={customerDivision} onChange={(e) => setCustomerDivision(e.target.value)} placeholder="Optional" />
          </div>
          <div>
            <Label>Location</Label>
            <Input value={customerLocation} onChange={(e) => setCustomerLocation(e.target.value)} placeholder="Optional" />
          </div>

          <div>
            <Label>MRC Division *</Label>
            <MultiSelectCombobox options={opt(divisionOpts)} value={divisions} onValueChange={setDivisions} placeholder="Select divisions…" />
          </div>
          <div>
            <Label>User Access</Label>
            <MultiSelectCombobox options={opt(userOpts)} value={accessUsers} onValueChange={setAccessUsers} placeholder="Grant access…" showSelected />
          </div>

          <div>
            <Label>Booking Branch *</Label>
            <MultiSelectCombobox options={opt(branchOpts)} value={bookingBranches} onValueChange={setBookingBranches} placeholder="Select branches…" showSelected />
          </div>
          <div>
            <Label>Billing Branch *</Label>
            {billingSame ? (
              <div className="flex h-10 items-center rounded-md border border-dashed px-3 text-sm text-muted-foreground">
                <span className="truncate">
                  {bookingBranches.length ? `Booking branches (${bookingBranches.length})` : 'Booking branches (none yet)'}
                </span>
              </div>
            ) : (
              <MultiSelectCombobox options={opt(branchOpts)} value={billingBranches} onValueChange={setBillingBranches} placeholder="Select branches…" showSelected />
            )}
            <label className="mt-2 flex w-fit items-center gap-2 text-sm cursor-pointer select-none">
              <Checkbox
                checked={billingSame}
                onCheckedChange={(v) => {
                  const on = v === true;
                  // turning it off keeps the booking branches as a starting point to adjust
                  if (!on) setBillingBranches(bookingBranches);
                  setBillingSame(on);
                }}
              />
              Same as booking branch
            </label>
          </div>

          <div>
            <Label>Contract Start Date *</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <Label>Contract End Date *</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>

          <div>
            <Label>Rate Type *</Label>
            <Select value={rateType} onValueChange={setRateType}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {RATE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Material per-kg Value</Label>
            <Input type="number" value={materialValue} onChange={(e) => setMaterialValue(e.target.value)} />
          </div>

          <div className="col-span-2">
            <Label>Type / Nature of Material *</Label>
            <Input value={materialType} onChange={(e) => setMaterialType(e.target.value)} />
          </div>

          <div>
            <Label>Mode of Packaging *</Label>
            <MultiSelectCombobox
              options={PACKAGING_MODES.map((m) => ({ value: m, label: m }))}
              value={packaging}
              onValueChange={setPackaging}
              placeholder="Select packaging…"
            />
          </div>
          {packaging.includes('Other') && (
            <div>
              <Label>Other Packaging</Label>
              <Input value={packagingOther} onChange={(e) => setPackagingOther(e.target.value)} />
            </div>
          )}

          {editing ? (
            <div className="col-span-2 border-t pt-3 space-y-1">
              <Label>Documents</Label>
              <p className="text-xs text-muted-foreground">Files can't be changed here.</p>
              <ul className="text-sm space-y-0.5">
                {editing.documents.map((d) => (
                  <li key={d.id} className="text-muted-foreground">
                    {d.document_type === 'main' ? (d.shared ? 'Main contract (shared terms)' : 'Main contract')
                      : d.document_type === 'rate_sheet' ? 'Rate sheet' : 'Other'}: {d.file_name}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
          <div className="col-span-2 border-t pt-3 space-y-3">
            {termsOpts.length > 0 && (
              <div className="space-y-1.5">
                <Label>Contract terms *</Label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="terms-mode" checked={termsMode === 'new'} onChange={() => setTermsMode('new')} />
                  Upload the contract document (new terms)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="terms-mode" checked={termsMode === 'existing'} onChange={() => setTermsMode('existing')} />
                  Use existing terms of this customer
                </label>
              </div>
            )}
            {termsMode === 'existing' ? (
              <div>
                <Label>Existing terms *</Label>
                <Combobox
                  options={termsOpts.map((t) => ({ value: String(t.id), label: termsOptionLabel(t) }))}
                  value={termsId}
                  onValueChange={setTermsId}
                  placeholder="Select terms…"
                  searchPlaceholder="Search…"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  The terms, billing and SLA fields are shared with the other contracts using them.
                  Only this contract's rate sheets are processed.
                </p>
              </div>
            ) : (
              <div>
                <Label>Main contract document (PDF) *</Label>
                <Input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setMainFile(e.target.files?.[0] || null)} />
              </div>
            )}
            <div>
              <Label>Rate sheets / supplemental annexures (optional)</Label>
              <Input type="file" multiple accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg"
                onChange={(e) => setRateSheets(Array.from(e.target.files || []))} />
              {rateSheets.length > 1 && (
                <p className="mt-1 text-xs text-muted-foreground">{rateSheets.length} files — rates from all of them are combined.</p>
              )}
            </div>
            <div>
              <Label>Other documents (optional)</Label>
              <Input type="file" multiple onChange={(e) => setOtherFiles(Array.from(e.target.files || []))} />
            </div>
          </div>
          )}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {editing ? (submitting ? 'Saving…' : 'Save changes') : (submitting ? 'Uploading…' : 'Upload & process')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadContractModal;
